import React, { useCallback, useEffect, useState } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useThemeStore } from "@/stores/theme-store";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import OfflineProvider from "@/components/offline/OfflineProvider";
import OfflineIndicator from "@/components/offline/OfflineIndicator";
import queryClient, { networkUtils } from "@/lib/queryClient";
import DebugUtils from "@/utils/debugUtils";
import StartupDebug from "@/utils/startupDebug";

interface AppProviderProps {
    children: React.ReactNode;
}

const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
    const { theme } = useThemeStore();
    const [_swRegistration, _setSwRegistration] =
        useState<ServiceWorkerRegistration | null>(null);
    const [_isUpdateAvailable, _setIsUpdateAvailable] = useState(false);

    // Initialize network listeners for query client and debug utilities
    useEffect(() => {
        networkUtils.addNetworkListeners();
        DebugUtils.initialize();
        DebugUtils.startPerformanceMonitoring();

        // Initialize comprehensive debugging in development
        if (process.env.NODE_ENV === "development") {
            StartupDebug.initialize();
            StartupDebug.startPeriodicReporting();
        }

        return () => {
            networkUtils.removeNetworkListeners();
        };
    }, []);

    // Theme management
    useEffect(() => {
        // Initialize theme on mount if not already set
        const getSystemTheme = () => {
            if (typeof window === "undefined") return "light";
            return window.matchMedia("(prefers-color-scheme: dark)").matches
                ? "dark"
                : "light";
        };

        const applyTheme = (currentTheme: string) => {
            const systemTheme = getSystemTheme();
            const isDark =
                currentTheme === "dark" ||
                (currentTheme === "system" && systemTheme === "dark");

            const root = document.documentElement;
            if (isDark) {
                root.classList.add("dark");
            } else {
                root.classList.remove("dark");
            }

            // Add/update meta theme-color for mobile browsers
            let metaThemeColor = document.querySelector(
                'meta[name="theme-color"]',
            );
            if (!metaThemeColor) {
                metaThemeColor = document.createElement("meta");
                metaThemeColor.setAttribute("name", "theme-color");
                document.head.appendChild(metaThemeColor);
            }
            metaThemeColor.setAttribute(
                "content",
                isDark ? "#1f2937" : "#ffffff",
            );
        };

        // Apply current theme
        applyTheme(theme);

        // Check for Google login success parameter
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("login") === "success") {
            // Show success message
            toast.success("Login berhasil! Selamat datang.");

            // Clean up URL without page reload
            // const cleanUrl = window.location.origin + window.location.pathname;
            // window.history.replaceState({}, "", cleanUrl);

            setTimeout(() => {
                // window.location.replace(
                //     window.location.origin + window.location.pathname,
                // );
                const cleanUrl = window.location.origin + window.location.pathname;
                window.history.replaceState({}, "", cleanUrl);
            }, 2000);
        }

        // Listen for system theme changes
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handleSystemThemeChange = () => {
            if (theme === "system") {
                applyTheme(theme);
            }
        };

        mediaQuery.addEventListener("change", handleSystemThemeChange);

        // Add viewport meta tag if not present
        let metaViewport = document.querySelector('meta[name="viewport"]');
        if (!metaViewport) {
            metaViewport = document.createElement("meta");
            metaViewport.setAttribute("name", "viewport");
            metaViewport.setAttribute(
                "content",
                "width=device-width, initial-scale=1",
            );
            document.head.appendChild(metaViewport);
        }

        return () => {
            mediaQuery.removeEventListener("change", handleSystemThemeChange);
        };
    }, [theme]);

    // Service Worker Registration
    useEffect(() => {
        if ("serviceWorker" in navigator) {
            // Register the service worker
            navigator.serviceWorker
                .register("/service-worker.js")
                .then((registration) => {
                    setSwRegistration(registration);

                    // Check for updates
                    registration.addEventListener("updatefound", () => {
                        const newWorker = registration.installing;
                        if (newWorker) {
                            newWorker.addEventListener("statechange", () => {
                                if (
                                    newWorker.state === "installed" &&
                                    navigator.serviceWorker.controller
                                ) {
                                    setIsUpdateAvailable(true);
                                    toast.info("Update available!", {
                                        description:
                                            "A new version is available. Refresh to update.",
                                        action: {
                                            label: "Update now",
                                            onClick: () => {
                                                // Send message to skip waiting
                                                newWorker.postMessage({
                                                    type: "SKIP_WAITING",
                                                });
                                                window.location.reload();
                                            },
                                        },
                                        duration: 10000, // 10 seconds
                                    });
                                }
                            });
                        }
                    });
                })
                .catch((error) => {
                    // ServiceWorker registration failed silently
                });

            // Handle controller change (when a new service worker takes over)
            navigator.serviceWorker.addEventListener("controllerchange", () => {
                // New service worker controller
            });

            // Listen for messages from service worker
            navigator.serviceWorker.addEventListener("message", (event) => {
                if (event.data && event.data.type === "CACHES_CLEARED") {
                    toast.success("Cache cleared successfully!");
                }
                if (event.data && event.data.type === "API_CACHE_CLEARED") {
                    toast.success("API cache cleared successfully!");
                }
            });
        }

        return () => {
            // Clean up if needed
            if (swRegistration) {
                // Any cleanup for service worker
            }
        };
    }, []);

    // Function to clear all caches
    const clearAllCaches = useCallback(() => {
        // Clear React Query cache
        queryClient.clear();

        // Clear Service Worker cache
        if (navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
                type: "CLEAR_CACHES",
            });
        }

        toast.success("All caches cleared successfully!");
    }, [queryClient]);

    // Function to clear API cache only
    const clearApiCache = useCallback(() => {
        // Clear React Query cache
        queryClient.invalidateQueries();

        // Clear Service Worker API cache
        if (navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
                type: "CLEAR_API_CACHE",
            });
        }

        toast.success("API cache cleared successfully!");
    }, [queryClient]);

    // Make these functions available through a global object
    useEffect(() => {
        if (typeof window !== "undefined") {
            window.ltnApp = {
                clearAllCaches,
                clearApiCache,
                queryClient, // Make query client available globally for debugging
                // Additional debugging utilities
                getQueryCache: () => queryClient.getQueryCache(),
                getQueryData: (key: any) => queryClient.getQueryData(key),
                getAllQueries: () => queryClient.getQueryCache().getAll(),
                invalidateAll: () => queryClient.invalidateQueries(),
                removeAll: () => queryClient.removeQueries(),
                // Network utilities
                isOnline: () => navigator.onLine,
                networkUtils,
                // Debug utilities
                debug: (window as any).ltnDebug,
                generateReport: () => DebugUtils.generateDebugReport(),
            };

            // Global utilities initialized silently

            // Log startup info with DebugUtils
            DebugUtils.log("info", "AppProvider", "Application initialized", {
                queryClientAvailable: !!window.ltnApp.queryClient,
                networkStatus: navigator.onLine ? "online" : "offline",
                userAgent: navigator.userAgent,
                url: window.location.href,
                debugMode: process.env.NODE_ENV === "development",
            });

            // Debug commands available silently in development
        }
    }, []); // EMERGENCY FIX: Empty dependency array to prevent infinite loop

    return (
        <QueryClientProvider client={queryClient}>
            <OfflineProvider
                autoShowModal={true}
                modalDelay={3000}
                useSimpleModal={true}
            >
                <OfflineIndicator variant="minimal" position="top" />
                {children}
                <Toaster />
            </OfflineProvider>
            <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
    );
};

// Add a type definition for the global window object
declare global {
    interface Window {
        ltnApp: {
            clearAllCaches: () => void;
            clearApiCache: () => void;
            queryClient: typeof queryClient;
            getQueryCache: () => any;
            getQueryData: (key: any) => any;
            getAllQueries: () => any[];
            invalidateAll: () => void;
            removeAll: () => void;
            isOnline: () => boolean;
            networkUtils: typeof networkUtils;
            debug: any;
            generateReport: () => any;
        };
    }
}

export default AppProvider;
